import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import {
  DocumentUploadModal,
  GeneralSelectInput,
  TextInput,
  NumberInput,
  MultiChoiceCheckboxGroup,
  RadioButtonGroup,
  MessageTextAreaInput,
} from "components/molecules";
import { Button } from "components/atoms";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import { useCurrency } from "contexts";
import {
  GenuineStudentFormValues,
  immediateFamilyMembersOptions,
  dependentTypesOptions,
  communityOrganizationTypesOptions,
  communityIntegrationOptions,
  employmentTypeOptions,
  mainIncomeSourceOptions,
  propertyTypesOptions,
  businessRoleOptions,
  FileWithType,
} from "types";

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["genuineStudentData"]),
};

interface GSHomeCountryTiesSectionProps {
  currentSubStage?: string;
  destinationCountry?: string;
}

const GSHomeCountryTiesSection: React.FC<GSHomeCountryTiesSectionProps> = ({
  currentSubStage = "family",
  destinationCountry = "Australia",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<GenuineStudentFormValues>();
  const { allCurrencies } = useCurrency();

  const currencyOptions = allCurrencies.map((code) => ({
    label: code,
    value: code,
  }));

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    marriageCertificate?: { id: string; fileName: string };
    birthCertificate?: { id: string; fileName: string };
    familyRegister?: { id: string; fileName: string };
    communityMembership?: { id: string; fileName: string };
    communityAppointment?: { id: string; fileName: string };
    dependentPassport?: { id: string; fileName: string };
    jointBankStatements?: { id: string; fileName: string };
    jointLease?: { id: string; fileName: string };
    jointUtilityBills?: { id: string; fileName: string };
    relationshipPhotos?: { id: string; fileName: string };
    statutoryDeclarations?: { id: string; fileName: string };
    dependentBirthCertificate?: { id: string; fileName: string };
    dependentAcademics?: { id: string; fileName: string };
    dependentEmployment?: { id: string; fileName: string };
    employmentLetter?: { id: string; fileName: string };
    payslips?: { id: string; fileName: string };
    incomeTax?: { id: string; fileName: string };
    propertyDeed?: { id: string; fileName: string };
    propertyTax?: { id: string; fileName: string };
    propertyValuation?: { id: string; fileName: string };
    leaseAgreement?: { id: string; fileName: string };
    businessRegistration?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024,
      enableMagicNumberValidation: false,
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Home country ties document uploaded successfully.",
  });

  const homeCountryDocTypes = [
    "MARRIAGE_CERTIFICATE",
    "BIRTH_CERTIFICATE",
    "FAMILY_REGISTER",
    "COMMUNITY_MEMBERSHIP_CERTIFCATE",
    "COMMUNITY_LETTER_OF_APPOINTMENT",
    "DEPENDENT_PASSPORT",
    "JOINT_BANK_STATEMENTS",
    "JOINT_LEASE",
    "JOINT_UTILITY_BILLS",
    "RELATIONSHIP_PHOTOS",
    "STATUTORY_DECLARATIONS",
    "DEPENDENT_BIRTH_CERTIFICATE",
    "DEPENDENT_ACADEMICS",
    "DEPENDENT_EMPLOYMENT",
    "EMPLOYMENT_LETTER",
    "PAYSLIPS",
    "INCOME_TAX",
    "PROPERTY_DEED",
    "PROPERTY_TAX_DOCUMENT",
    "PROPERTY_VALUATION",
    "LEASE_AGREEMENT",
    "BUSINESS_REGISTRATION_CERTIFICATE",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(homeCountryDocTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs =
            await documentService.fetchDocumentsWithMapping(
              homeCountryDocTypes,
            );

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const getDocumentFields = (filterByStage?: string) => {
    const bringingDependents =
      values.bringingDependents === true &&
      (values.numberOfDependents ?? 0) > 0;

    const allDocuments = [
      {
        id: "marriagecertificate",
        label: "Marriage Certificate",
        description: "Official marriage certificate showing family ties",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.hasImmediateFamilyHome === true || bringingDependents,
        uploaded: !!uploadedDocuments.marriageCertificate,
        stage: "family",
      },
      {
        id: "birthcertificate",
        label: "Birth Certificate",
        description: "Official birth certificate proving family relationships",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.hasImmediateFamilyHome === true || bringingDependents,
        uploaded: !!uploadedDocuments.birthCertificate,
        stage: "family",
      },
      {
        id: "familyregister",
        label: "Family Register",
        description:
          "Official family register or household registration document",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.hasImmediateFamilyHome === true || bringingDependents,
        uploaded: !!uploadedDocuments.familyRegister,
        stage: "family",
      },
      {
        id: "communitymembership",
        label: "Community Membership Certificate",
        description: "Certificate of membership in community organizations",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.hasCommunityInvolvement === true,
        uploaded: !!uploadedDocuments.communityMembership,
        stage: "family",
      },
      {
        id: "communityappointment",
        label: "Community Letter of Appointment",
        description:
          "Letter of appointment to community organization positions",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.hasCommunityInvolvement === true,
        uploaded: !!uploadedDocuments.communityAppointment,
        stage: "family",
      },
      {
        id: "dependentpassport",
        label: "Dependent's Passport",
        description:
          "All pages of dependent's current passport (for visa application)",
        accept: ".pdf,.jpg,.jpeg",
        required: values.bringingDependents === true,
        uploaded: !!uploadedDocuments.dependentPassport,
        stage: "family",
      },
      {
        id: "jointbankstatements",
        label: "Joint Bank Account Statements",
        description: "Joint bank account statements (6-12 months)",
        accept: ".pdf,.jpg,.jpeg",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.jointBankStatements,
        stage: "family",
      },
      {
        id: "jointlease",
        label: "Joint Lease/Rental Agreement",
        description: "Joint lease or rental agreement showing cohabitation",
        accept: ".pdf,.doc,.docx",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.jointLease,
        stage: "family",
      },
      {
        id: "jointutilitybills",
        label: "Joint Utility Bills",
        description: "Joint utility bills (electricity, water, internet)",
        accept: ".pdf,.jpg,.jpeg",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.jointUtilityBills,
        stage: "family",
      },
      {
        id: "relationshipphotos",
        label: "Relationship Photos",
        description: "Photos together (at wedding, with family, over time)",
        accept: ".pdf,.jpg,.jpeg,.zip",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.relationshipPhotos,
        stage: "family",
      },
      {
        id: "statutorydeclarations",
        label: "Statutory Declarations",
        description:
          "Statutory declarations from family/friends about relationship",
        accept: ".pdf,.doc,.docx",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.statutoryDeclarations,
        stage: "family",
      },
      {
        id: "dependentbirthcertificate",
        label: "Dependent's Birth Certificate",
        description: "Birth certificate for dependent children",
        accept: ".pdf,.jpg,.jpeg",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.dependentBirthCertificate,
        stage: "family",
      },
      {
        id: "dependentacademics",
        label: "Dependent's Academic Documents",
        description: "Dependent's academic certificates and transcripts",
        accept: ".pdf,.jpg,.jpeg",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.dependentAcademics,
        stage: "family",
      },
      {
        id: "dependentemployment",
        label: "Dependent's Employment Documents",
        description: "Dependent's employment references and documents",
        accept: ".pdf,.doc,.docx",
        required: bringingDependents,
        uploaded: !!uploadedDocuments.dependentEmployment,
        stage: "family",
      },
      {
        id: "employmentletter",
        label: "Employment Letter",
        description: "Current employment verification letter from employer",
        accept: ".pdf,.doc,.docx",
        required: values.currentlyEmployed === true,
        uploaded: !!uploadedDocuments.employmentLetter,
        stage: "professional",
      },
      {
        id: "payslips",
        label: "Payslips",
        description:
          "Recent payslips (last 3 months) showing employment income",
        accept: ".pdf,.jpg,.jpeg",
        required: values.currentlyEmployed === true,
        uploaded: !!uploadedDocuments.payslips,
        stage: "professional",
      },
      {
        id: "incometax",
        label: "Income Tax Return",
        description: "Recent income tax return or assessment notice",
        accept: ".pdf,.jpg,.jpeg",
        required: values.currentlyEmployed === true,
        uploaded: !!uploadedDocuments.incomeTax,
        stage: "professional",
      },
      {
        id: "propertydeed",
        label: "Property Deed",
        description: "Property ownership deed or title document",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.ownsProperty === true,
        uploaded: !!uploadedDocuments.propertyDeed,
        stage: "professional",
      },
      {
        id: "propertytax",
        label: "Property Tax Document",
        description: "Property tax receipt or assessment showing ownership",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.ownsProperty === true,
        uploaded: !!uploadedDocuments.propertyTax,
        stage: "professional",
      },
      {
        id: "propertyvaluation",
        label: "Property Valuation Report",
        description:
          "Official property valuation report (minimum AUD 10 million worth)",
        accept: ".pdf,.doc,.docx",
        required: values.ownsProperty === true,
        uploaded: !!uploadedDocuments.propertyValuation,
        stage: "professional",
      },
      {
        id: "leaseagreement",
        label: "Lease Agreement",
        description: "Property lease agreement if renting out owned property",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.ownsProperty === true,
        uploaded: !!uploadedDocuments.leaseAgreement,
        stage: "professional",
      },
      {
        id: "businessregistration",
        label: "Business Registration Certificate",
        description:
          "Business registration certificate or incorporation documents",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.ownsBusiness === true,
        uploaded: !!uploadedDocuments.businessRegistration,
        stage: "professional",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const renderFamilySection = () => (
    <div className="family-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Family & Social</h4>
        </div>
        <div className="form-fields">
          <div className="form-field">
            <RadioButtonGroup
              name="hasImmediateFamilyHome"
              label="Do you have immediate family in your home country?"
              value={values.hasImmediateFamilyHome}
              onChange={(value) => {
                setFieldValue("hasImmediateFamilyHome", value);
                setFieldTouched("hasImmediateFamilyHome", true);
                if (value === false) {
                  setFieldValue("immediateFamilyMembers", []);
                }
              }}
            />
            {values.hasImmediateFamilyHome === true && (
              <MultiChoiceCheckboxGroup
                name="immediateFamilyMembers"
                label="Select all that apply:"
                options={immediateFamilyMembersOptions}
                value={values.immediateFamilyMembers || []}
                onChange={(values: string[]) =>
                  setFieldValue("immediateFamilyMembers", values)
                }
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="hasDependents"
              label="Are any family members financially dependent on you?"
              value={values.hasDependents}
              onChange={(value) => {
                setFieldValue("hasDependents", value);
                setFieldTouched("hasDependents", true);
                if (value === false) {
                  setFieldValue("dependentTypes", []);
                }
              }}
            />
            {values.hasDependents === true && (
              <MultiChoiceCheckboxGroup
                name="dependentTypes"
                label="Select all that apply:"
                options={dependentTypesOptions}
                value={values.dependentTypes || []}
                onChange={(values: string[]) =>
                  setFieldValue("dependentTypes", values)
                }
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="bringingDependents"
              label="Will you be bringing any dependents/accompanying persons to Australia?"
              value={values.bringingDependents}
              onChange={(value) => {
                setFieldValue("bringingDependents", value);
                setFieldTouched("bringingDependents", true);
                if (value === false) {
                  setFieldValue("numberOfDependents", 0);
                  setFieldValue("understandDependentWorkRights", null);
                  setFieldValue("dependentHealthInsuranceArranged", null);
                }
              }}
            />
            {values.bringingDependents === true && (
              <>
                <NumberInput
                  id="numberOfDependents"
                  label="Number of dependents/accompanying persons:"
                  placeholder="Enter number"
                  getFieldProps={() => ({
                    value: values.numberOfDependents || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.numberOfDependents}
                  touched={touched.numberOfDependents}
                />

                <div
                  className="info-box"
                  style={{
                    padding: "1rem",
                    backgroundColor: "#f0f7ff",
                    border: "1px solid #bdd7ff",
                    borderRadius: "4px",
                    marginTop: "1rem",
                  }}
                >
                  <p>
                    <strong>Note:</strong> You will need to provide separate
                    details for each dependent including:
                  </p>
                  <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                    <li>Passport copies (for visa application)</li>
                    <li>Birth certificates (for children)</li>
                    <li>Marriage certificate (for spouse)</li>
                    <li>De facto relationship evidence (if applicable)</li>
                    <li>Academic/employment documents (for spouse)</li>
                  </ul>
                </div>

                <RadioButtonGroup
                  name="understandDependentWorkRights"
                  label="Do you understand the work rights for your dependents in Australia?"
                  value={values.understandDependentWorkRights}
                  onChange={(value) => {
                    setFieldValue("understandDependentWorkRights", value);
                    setFieldTouched("understandDependentWorkRights", true);
                  }}
                />

                <RadioButtonGroup
                  name="dependentHealthInsuranceArranged"
                  label="Have you arranged health insurance (OSHC/OVHC) for your dependents?"
                  value={values.dependentHealthInsuranceArranged}
                  onChange={(value) => {
                    setFieldValue("dependentHealthInsuranceArranged", value);
                    setFieldTouched("dependentHealthInsuranceArranged", true);
                  }}
                />
              </>
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="hasFamilyInHost"
              label={`Do you have immediate family in the  ${destinationCountry}?`}
              value={values.hasFamilyInHost}
              onChange={(value) => {
                setFieldValue("hasFamilyInHost", value);
                setFieldTouched("hasFamilyInHost", true);
                if (value === false) {
                  setFieldValue("familyInHostRelationship", "");
                }
              }}
            />
            {values.hasFamilyInHost === true && (
              <MessageTextAreaInput
                id="familyInHostRelationship"
                label="Specify relationship:"
                placeholder="Describe the relationship"
                rows="2"
                maxLength={100}
                getFieldProps={() => ({
                  value: values.familyInHostRelationship || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.familyInHostRelationship}
                touched={touched.familyInHostRelationship}
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="hasCommunityInvolvement"
              label="Are you involved in community organizations in your home country?"
              value={values.hasCommunityInvolvement}
              onChange={(value) => {
                setFieldValue("hasCommunityInvolvement", value);
                setFieldTouched("hasCommunityInvolvement", true);
                if (value === false) {
                  setFieldValue("communityOrganizationTypes", []);
                }
              }}
            />
            {values.hasCommunityInvolvement === true && (
              <MultiChoiceCheckboxGroup
                name="communityOrganizationTypes"
                label="Select all that apply:"
                options={communityOrganizationTypesOptions}
                value={values.communityOrganizationTypes || []}
                onChange={(values: string[]) =>
                  setFieldValue("communityOrganizationTypes", values)
                }
                onOtherChange={(value) =>
                  setFieldValue("otherCommunityOrganization", value)
                }
                showOther={true}
                otherPlaceholder="Please specify"
                required={true}
              />
            )}
          </div>

          <GeneralSelectInput
            id="communityIntegration"
            label="How integrated are you in your home country community?"
            options={communityIntegrationOptions}
            value={values.communityIntegration || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.communityIntegration}
            touched={touched.communityIntegration}
          />

          <MessageTextAreaInput
            id="familyCommunityTies"
            label="Current Family & Community Ties (150 words max)"
            placeholder="Describe your family, community, and social connections in your home country. Include specific details about relationships, responsibilities, and involvement that demonstrate your ties."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.familyCommunityTies || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.familyCommunityTies}
            touched={touched.familyCommunityTies}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="12rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Home Country Ties Documents"
        description="Please attach documents that prove your family ties to your home country:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderProfessionalSection = () => (
    <div className="professional-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Professional & Assets</h4>
        </div>
        <div className="form-fields">
          <div className="form-field">
            <RadioButtonGroup
              name="currentlyEmployed"
              label="Are you currently employed?"
              value={values.currentlyEmployed}
              onChange={(value) => {
                setFieldValue("currentlyEmployed", value);
                setFieldTouched("currentlyEmployed", true);
                if (value === false) {
                  setFieldValue("employmentType", "");
                  setFieldValue("yearsInPosition", null);
                  setFieldValue("industry", "");
                  setFieldValue("positionHeld", null);
                  setFieldValue("mainIncomeSource", "");
                }
              }}
            />
            {values.currentlyEmployed === true && (
              <>
                <GeneralSelectInput
                  id="employmentType"
                  label="Employment type:"
                  options={employmentTypeOptions}
                  value={values.employmentType || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.employmentType}
                  touched={touched.employmentType}
                />
                <NumberInput
                  id="yearsInPosition"
                  label="Years in position:"
                  placeholder="Enter number of years"
                  getFieldProps={() => ({
                    value: values.yearsInPosition || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.yearsInPosition}
                  touched={touched.yearsInPosition}
                />
                <TextInput
                  id="industry"
                  label="Industry:"
                  placeholder="Enter your industry"
                  getFieldProps={() => ({
                    value: values.industry || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.industry}
                  touched={touched.industry}
                />
                <RadioButtonGroup
                  name="positionHeld"
                  label="Will position be held for you?"
                  value={values.positionHeld}
                  onChange={(value) => {
                    setFieldValue("positionHeld", value);
                    setFieldTouched("positionHeld", true);
                  }}
                />
              </>
            )}
            {values.currentlyEmployed === false && (
              <>
                <GeneralSelectInput
                  id="mainIncomeSource"
                  label="Main income source:"
                  options={mainIncomeSourceOptions}
                  value={values.mainIncomeSource || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.mainIncomeSource}
                  touched={touched.mainIncomeSource}
                />
                {values.mainIncomeSource === "other" && (
                  <TextInput
                    id="otherMainIncomeSource"
                    label="Please specify:"
                    placeholder="Please specify"
                    getFieldProps={() => ({
                      value: values.otherMainIncomeSource || "",
                      onChange: handleChange,
                      onBlur: handleBlur,
                    })}
                    error={errors.otherMainIncomeSource}
                    touched={touched.otherMainIncomeSource}
                  />
                )}
              </>
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="ownsProperty"
              label="Do you own property in your home country?"
              value={values.ownsProperty}
              onChange={(value) => {
                setFieldValue("ownsProperty", value);
                setFieldTouched("ownsProperty", true);
                if (value === false) {
                  setFieldValue("propertyTypes", []);
                  setFieldValue("propertyValuationAmount", 0);
                  setFieldValue("propertyValuationCurrency", "");
                }
              }}
            />
            {values.ownsProperty === true && (
              <>
                <MultiChoiceCheckboxGroup
                  name="propertyTypes"
                  label="Select all that apply:"
                  options={propertyTypesOptions}
                  value={values.propertyTypes || []}
                  onChange={(values: string[]) =>
                    setFieldValue("propertyTypes", values)
                  }
                />

                <NumberInput
                  id="propertyValuationAmount"
                  label="Property valuation amount:"
                  placeholder="Enter total property value (minimum AUD 10 million)"
                  getFieldProps={() => ({
                    value: values.propertyValuationAmount || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.propertyValuationAmount}
                  touched={touched.propertyValuationAmount}
                />

                <GeneralSelectInput
                  id="propertyValuationCurrency"
                  label="Currency:"
                  options={currencyOptions}
                  value={values.propertyValuationCurrency || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.propertyValuationCurrency}
                  touched={touched.propertyValuationCurrency}
                />
              </>
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="ownsBusiness"
              label="Do you own or co-own a business in your home country?"
              value={values.ownsBusiness}
              onChange={(value) => {
                setFieldValue("ownsBusiness", value);
                setFieldTouched("ownsBusiness", true);
                if (value === false) {
                  setFieldValue("businessRole", "");
                }
              }}
            />
            {values.ownsBusiness === true && (
              <GeneralSelectInput
                id="businessRole"
                label="Your role:"
                options={businessRoleOptions}
                value={values.businessRole || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.businessRole}
                touched={touched.businessRole}
              />
            )}
          </div>

          <RadioButtonGroup
            name="hasInvestments"
            label="Do you have investments or financial assets in your home country?"
            value={values.hasInvestments}
            onChange={(value) => {
              setFieldValue("hasInvestments", value);
              setFieldTouched("hasInvestments", true);
            }}
          />

          <MessageTextAreaInput
            id="employmentEconomicSituation"
            label="Employment & Economic Situation (150 words max)"
            placeholder="Describe your current employment and economic circumstances. Include employer details, position, responsibilities, and how long you've been employed. If not employed, explain your financial support sources. Detail any property, business, or investment commitments requiring your presence."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.employmentEconomicSituation || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.employmentEconomicSituation}
            touched={touched.employmentEconomicSituation}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="12rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Home Country Ties Documents"
        description="Please attach documents that prove your professional and asset ties to your home country:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  switch (currentSubStage) {
    case "family":
      return renderFamilySection();
    case "professional":
      return renderProfessionalSection();
    default:
      return renderFamilySection();
  }
};

export default GSHomeCountryTiesSection;
